class ReadMore extends HTMLElement {
  connectedCallback() {
    this._target = this.firstElementChild;
    if (!this._target) return;

    // Check if read-more is disabled
    const state = this.getAttribute("state");
    if (state === "off" || state === "false" || state === "no") {
      return; // Do nothing, component is inactive
    }

    this._collapsedClass = this.getAttribute("collapsed-class") || "line-clamp";
    this._expandedText = this.getAttribute("expanded-text") || "Read less";
    this._collapsedText = this.getAttribute("collapsed-text") || "Read more";

    this._target.classList.add(this._collapsedClass);

    // Set line count if specified
    const lines = this.getAttribute("lines");
    if (lines) {
      this._target.style.setProperty("--line-clamp-count", lines);
    }

    // Generate the toggle button
    this._createToggleButton();

    // Delay visibility check until after render
    requestAnimationFrame(() => {
      this._updateVisibility();
    });

    // Add click handler
    this._button.addEventListener("click", this._onToggleClick);

    // Recheck on window resize
    window.addEventListener("resize", () => this._updateVisibility());
  }

  disconnectedCallback() {
    this._button?.removeEventListener("click", this._onToggleClick);
  }

  _createToggleButton() {
    this._wrapper = document.createElement("span");
    this._wrapper.className = "inline-block";

    this._button = document.createElement("a");
    const includeUnderline =
      this.getAttribute("underline") === "yes" ||
      this.getAttribute("underline") === "true";

    this._button.className = `text-with-icon group${includeUnderline ? " underline" : ""}`;
    this._button.href = "#";
    const showChevron = !["hide", "no", "false"].includes(
      this.getAttribute("chevron"),
    );

    this._button.innerHTML = `
      <span class="text" data-read-more-text>${this._collapsedText}</span>
      ${
        showChevron
          ? `<span class="circle-chevron group-hover:colors">
        <svg role="presentation" 
            focusable="false" 
            width="5" 
            height="8" 
            class="icon icon-chevron-right-small reverse-icon" 
            viewBox="0 0 5 8" 
            data-read-more-chevron
            style="transition: transform 0.3s ease; transform: rotate(90deg);">
          <path d="m.75 7 3-3-3-3" fill="none" stroke="currentColor" stroke-width="1.5"></path>
        </svg>
      </span>`
          : ""
      }
    `;

    this._wrapper.appendChild(this._button);
    this.appendChild(this._wrapper);

    this._textSpan = this._button.querySelector("[data-read-more-text]");
    this._chevron = this._button.querySelector("[data-read-more-chevron]");
  }

  _onToggleClick = (event) => {
    event.preventDefault();
    this._toggle();
  };

  _toggle() {
    if (this._target.classList.contains(this._collapsedClass)) {
      this._target.classList.remove(this._collapsedClass);
      this._textSpan.textContent = this._expandedText;
      if (this._chevron) this._chevron.style.transform = "rotate(270deg)";
    } else {
      this._target.classList.add(this._collapsedClass);
      this._textSpan.textContent = this._collapsedText;
      if (this._chevron) this._chevron.style.transform = "rotate(90deg)";
    }
  }

  _updateVisibility() {
    if (
      this._target.classList.contains(this._collapsedClass) &&
      this._target.scrollHeight > this._target.clientHeight
    ) {
      this._wrapper.hidden = false;
    } else {
      this._wrapper.hidden = true;
    }
  }
}

if (!window.customElements.get("read-more")) {
  window.customElements.define("read-more", ReadMore);
}

/**
 * Write the size line-item properties from a selected size option element
 * (a `.popover-listbox__option` button or a `label.block-swatch`).
 *
 * Same extraction as three pre-existing copies, all deliberately left alone:
 * `assets/sections.js.liquid:2199-2220` (tab handler), `assets/theme.js.liquid:4614-4635`
 * (BuyButtons) and `assets/theme.js.liquid:5546-5567` (VariantPicker). This copy is the
 * only one that null-guards its lookups and the only one scoped to a single form.
 *
 * @param {Element} optionEl the size option the shopper clicked
 * @param {ParentNode} [root] the form to write into; defaults to the whole document.
 *   `#customer-size` / `#cart-size-display` are rendered once per buy-buttons instance
 *   (snippets/buy-buttons.liquid:101-102), so a page with a quick-buy drawer has several.
 */
export function writeSizeProperties(optionEl, root = document) {
  const span = optionEl?.querySelector(".label-text.active span");
  if (!span) return;

  const checkoutLabel =
    span.getAttribute("data-checkout-label") || span.textContent;
  const cartDisplay = span.getAttribute("data-cart-display") || checkoutLabel;

  // Split at colon to separate name and value for checkout.
  // Some standards ship an unprefixed label (the EU tab's data-checkout-label is
  // just "36", not "EU: 36"), which would yield `properties[36]` with an empty
  // value — a junk line-item property on the packing slip. Write nothing rather
  // than that; the existing tab handler only avoids it by never running until a
  // tab is clicked. Fixing the label data is a metafield change, not a code one.
  const parts = checkoutLabel.split(":");
  const name = parts[0].trim();
  const value = parts.slice(1).join(":").trim();

  // Skip only the checkout property when the label is unprefixed — `_cart_display`
  // below is independent of it and must still be written, or the cart line loses
  // its size text (the legacy writers set it unconditionally).
  const customerSize = root.querySelector("#customer-size");
  if (customerSize && parts.length >= 2 && name && value) {
    customerSize.setAttribute("name", `properties[${name}]`);
    customerSize.setAttribute("value", value);
  }

  const cartDisplayInput = root.querySelector("#cart-size-display");
  if (cartDisplayInput) {
    cartDisplayInput.setAttribute("value", cartDisplay);
  }
}

/**
 * <size-gate> — blocks add-to-cart until the shopper explicitly confirms a size.
 *
 * Rendered by `snippets/variant-picker.liquid` inside every gated <variant-picker>,
 * wrapping the pre-rendered (hidden) warning notice. The picker carries
 * `require-size-selection`, and `size-confirmed` once a size is chosen.
 *
 * Confirmation listens for `click`, never `listbox:change` / `variant:change`:
 * `Listbox.attributeChangedCallback` guards on `oldValue !== null && newValue !== oldValue`,
 * so clicking the already-active phantom option fires nothing at all. Same for the
 * already-checked phantom radio in block mode, which fires no `change`.
 */
class SizeGate extends HTMLElement {
  connectedCallback() {
    this._picker = this.closest("variant-picker");
    if (!this._picker) return;

    // The phantom variant must never reach the URL. `?variant=<phantom>` makes
    // `product.selected_variant` non-nil on the next load, so `variant-picker.liquid`
    // renders `size-confirmed` and the gate opens without the shopper ever choosing a
    // size. `VariantPicker.updateUrl` is a live `hasAttribute` read
    // (theme.js.liquid:5585), so dropping the attribute here and restoring it in
    // `_confirm()` is enough — no theme.js change needed.
    if (this._blocked && this._picker.hasAttribute("update-url")) {
      this._urlWasManaged = true;
      this._picker.removeAttribute("update-url");
    }

    // The size this shopper confirmed on an EARLIER visit. Read once, before any
    // confirmation this page view can record.
    this._initialSavedSize = this._readSavedSize();

    // Capture phase: fires before ProductForm's target-phase submit listener,
    // so preventDefault() + stopPropagation() cancels the add.
    document.addEventListener("submit", this._onSubmit, true);
    this._picker.addEventListener("click", this._onConfirmClick);
    // `variant:change` is dispatched on the form (theme.js.liquid:5615), and the
    // form is not an ancestor of the picker — the picker's inputs only reference
    // it via `form=`. So it has to be listened for on the form itself.
    this._form = document.forms[this._picker.getAttribute("form")] || null;
    this._form?.addEventListener("variant:change", this._onVariantChange);

    // Our siblings inside <variant-picker> are not parsed yet at this point.
    requestAnimationFrame(() => {
      this._resolve();
      this._restoreSavedSize();
      this._resetLabelIfBlocked();
    });
  }

  disconnectedCallback() {
    document.removeEventListener("submit", this._onSubmit, true);
    this._picker?.removeEventListener("click", this._onConfirmClick);
    this._form?.removeEventListener("variant:change", this._onVariantChange);
  }

  /**
   * The size this shopper previously CONFIRMED, from the gate's own key.
   *
   * Deliberately NOT `size_prefs_<group>`: `BuyButtons._onVariantChanged`
   * (theme.js.liquid:4370) rewrites that key on EVERY variant change, including a
   * colour click that never touched the size. An unconfirmed colour click therefore
   * persists the phantom size, and the next page load reads it back as a prior
   * confirmation. `size_confirmed_<group>` is written only by `_recordConfirmation`,
   * so nothing but a real confirmation can ever set it.
   *
   * Not seeded from `size_prefs_`: that value may already hold a phantom, which would
   * migrate the exact bug this closes. Shoppers who chose a size before this key
   * existed confirm once more, then never again.
   */
  _readSavedSize() {
    const group = this._picker.getAttribute("size-group");
    if (!group) return null;
    try {
      return (
        JSON.parse(localStorage.getItem(`size_confirmed_${group}`) || "{}")
          .size || null
      );
    } catch {
      return null;
    }
  }

  /**
   * Two ways the saved size can already be applied by the time we look:
   * the picker restored it before this element upgraded (so `variant:change`
   * fired before our listener existed), or the saved size equals the phantom and
   * `VariantPicker.selectVariant` early-returned without firing anything at all.
   * Both show up as "the picker's current size already is the saved size".
   */
  _restoreSavedSize() {
    if (!this._blocked) return;
    const saved = this._initialSavedSize;
    if (saved && saved === this._currentSizeValue()) {
      this._confirm(this._optionForValue(saved));
    }
  }

  /** The size the picker currently holds, whichever mode is rendered. */
  _currentSizeValue() {
    if (this._blockValues) {
      return this._blockValues.querySelector("input:checked")?.value || null;
    }
    return (
      this._popover?.querySelector('input[type="hidden"][name^="option"]')
        ?.value ||
      this._picker
        .querySelector('.popover-listbox__option[aria-selected="true"]')
        ?.getAttribute("value") ||
      null
    );
  }

  /** The clickable option element for a size value, in whichever mode is rendered. */
  _optionForValue(value) {
    if (value == null) return null;
    const escaped = CSS.escape(value);
    const input = this._blockValues?.querySelector(
      `input[value="${escaped}"]`,
    );
    if (input) {
      return this._blockValues.querySelector(`label[for="${CSS.escape(input.id)}"]`);
    }
    return this._picker.querySelector(
      `.popover-listbox__option[value="${escaped}"]`,
    );
  }

  /**
   * The picker restored a saved size preference. That is a real prior selection,
   * so the gate opens — but only now, after the label reflects it, not during the
   * load gap (Q3).
   */
  _onVariantChange = () => {
    if (!this._blocked) return;
    const saved = this._initialSavedSize;
    // Only when the picker has actually landed on the previously-saved size.
    // A colour change fires this too, and must not open the gate.
    if (saved && saved === this._currentSizeValue()) {
      this._confirm(this._optionForValue(saved));
      return;
    }
    // Still blocked, and BuyButtons has just painted a size onto the button that
    // the shopper never confirmed. Put the placeholder back.
    this._resolve();
    this._resetLabelIfBlocked();
  };

  /**
   * Restore the placeholder label whenever the gate is blocked.
   *
   * `_getInitialVariantId` (theme.js.liquid) still restores a size from
   * `size_prefs_<group>` and `BuyButtons._onVariantChanged` paints it onto the button,
   * but the gate no longer treats that as a confirmation. Without this the page reads
   * "EU 37" and "Please select a size" at the same time.
   *
   * The placeholder comes from the `placeholder` attribute, not a DOM snapshot: theme.js
   * loads before this module, so by the time the element upgrades the label may already
   * have been repainted and there is nothing left to snapshot.
   *
   * Block mode has no `button.select`, so this is a no-op there — the unconfirmed state
   * is carried by `--size-unconfirmed`, which BuyButtons never touches.
   */
  _resetLabelIfBlocked() {
    if (!this._blocked) return;
    const placeholder = this.getAttribute("placeholder");
    const button = this._picker.querySelector("button.select");
    const wrapper = button?.querySelector("span.option-wrapper");
    if (!placeholder || !wrapper) return;

    wrapper.textContent = placeholder;
    button.classList.remove("is-disabled", "low-stock", "pre-order");
    button.classList.add("unsure");
    // Re-cache: `_resolve` may have missed the button if `unsure` was already stripped,
    // and `_syncButtonLabel` refuses to run without it.
    this._button = button;
  }

  /**
   * Cache the elements the gate touches, while the server-rendered placeholder
   * classes are still intact (`unsure` is stripped by BuyButtons on variant change).
   */
  _resolve() {
    if (this._resolved) return;
    this._resolved = true;

    this._notice = this.querySelector(".variant-picker__size-notice");
    this._blockValues = this._picker.querySelector(
      ".variant-picker__option-values--size-unconfirmed",
    );
    this._button = this._picker.querySelector("button.select.unsure");
    this._popover =
      this._button?.parentElement.querySelector("x-popover") || null;
  }

  get _blocked() {
    return (
      this._picker.hasAttribute("require-size-selection") &&
      !this._picker.hasAttribute("size-confirmed")
    );
  }

  _onSubmit = (event) => {
    if (!this._blocked) return;
    // Several gated pickers (quick-buy drawers) can share a page — only react to
    // the form this picker belongs to. Read the attribute, not `.id`: the form
    // contains `<input name="id">`, and named-property access shadows the
    // `id` property with that element.
    if (
      event.target.getAttribute("id") !== this._picker.getAttribute("form")
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this._resolve();
    if (this._notice) this._notice.hidden = false;

    if (this._popover) {
      this._popover.show();
      // Under 1000px the popover animates in as a viewport-pinned bottom sheet,
      // so it is visible without scrolling.
      if (window.innerWidth > 999) {
        this._picker.scrollIntoView({ block: "center" });
      }
    } else {
      this._picker.scrollIntoView({ block: "center" });
    }
  };

  _onConfirmClick = (event) => {
    if (!this._blocked) return;
    this._resolve();

    const option = event.target.closest(
      ".popover-listbox__option, label.block-swatch",
    );
    if (!option) return;

    // Only the size option confirms — colour pickers in the same <variant-picker>
    // must not.
    if (option.matches("label.block-swatch")) {
      if (!this._blockValues?.contains(option)) return;
    } else if (!this._popover?.contains(option)) {
      return;
    }

    this._confirm(option);
  };

  _confirm(option) {
    this._picker.setAttribute("size-confirmed", "");
    if (this._notice) this._notice.hidden = true;
    this._blockValues?.classList.remove(
      "variant-picker__option-values--size-unconfirmed",
    );
    this._syncButtonLabel(option);
    writeSizeProperties(option, this._form ?? document);
    this._recordConfirmation(option);
    this._restoreUrlUpdates();
  }

  /** The size value an option element stands for, in whichever mode is rendered. */
  _valueForOption(option) {
    if (!option) return null;
    if (option.matches("label.block-swatch")) {
      const id = option.getAttribute("for");
      if (!id) return null;
      return (
        this._blockValues?.querySelector(`#${CSS.escape(id)}`)?.value ?? null
      );
    }
    return option.getAttribute("value");
  }

  /**
   * Persist the confirmation under the gate's own key — see `_readSavedSize` for why
   * `size_prefs_<group>` cannot be trusted for this.
   *
   * The value comes from the clicked option, not `_currentSizeValue()`: in listbox mode
   * the picker's `aria-selected` is moved by Listbox's own handler and the ordering
   * against this bubbled click listener is not guaranteed.
   */
  _recordConfirmation(option) {
    const group = this._picker.getAttribute("size-group");
    const size = this._valueForOption(option) ?? this._currentSizeValue();
    if (!group || !size) return;
    try {
      localStorage.setItem(`size_confirmed_${group}`, JSON.stringify({ size }));
    } catch {
      // Private mode or quota. The gate still works; it just re-asks next visit.
    }
  }

  /**
   * Hand URL updating back to the picker and write the variant the shopper actually
   * landed on. Without the backfill the URL stays stale until their next variant
   * change, so confirm-then-copy yields a link to the wrong variant.
   */
  _restoreUrlUpdates() {
    if (!this._urlWasManaged) return;
    this._urlWasManaged = false;
    this._picker.setAttribute("update-url", "");

    const id = this._form?.querySelector('input[name="id"]')?.value;
    if (!id || !history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set("variant", id);
    history.replaceState({ path: url.toString() }, "", url.toString());
  }

  /**
   * Dropdown mode: the placeholder click may not fire `listbox:change`, so write the
   * label ourselves. Mirrors `BuyButtons._onVariantChanged` (theme.js.liquid:4415-4440).
   */
  _syncButtonLabel(option) {
    // `unsure` is only present while the label still holds the placeholder.
    if (!option || !this._button?.classList.contains("unsure")) return;

    const wrapper = this._button.querySelector("span.option-wrapper");
    if (wrapper) wrapper.innerHTML = option.innerHTML;

    this._button.classList.remove("is-disabled", "low-stock", "unsure");
    if (option.classList.contains("is-disabled")) {
      this._button.classList.add("is-disabled");
    }
    if (option.classList.contains("low-stock")) {
      this._button.classList.add("low-stock");
    }
    if (option.classList.contains("pre-order")) {
      this._button.classList.add("pre-order");
    }
  }
}

if (!window.customElements.get("size-gate")) {
  window.customElements.define("size-gate", SizeGate);
}

/**
 * <variant-guard> - make the variant the form posts match the size the shopper picked.
 *
 * `VariantPicker.connectedCallback` (theme.js.liquid:5451) attaches its option `change`
 * listeners only *after* `await ProductLoader.load(...)`, which is a live fetch of
 * `/products/<handle>.js`. A size clicked before that request lands updates the option
 * input, the dropdown label and - through <size-gate> - the size line-item properties,
 * but never reaches `selectVariant`, so `input[name="id"]` keeps the server-rendered
 * first variant.
 *
 * Nothing reconciles it afterwards: when the JSON finally arrives `_getInitialVariantId`
 * falls back to that same stale id and `selectVariant` early-returns on
 * `previousVariant.id === id`, so the page view stays wrong however long the shopper
 * browses. They add "UK 6 (EU 39)", the cart line reads "UK 6 (EU 39)", and the SKU
 * shipped is EU 36 - order #207647.
 *
 * The map is rendered server-side (snippets/variant-picker.liquid). That is the whole
 * point: the network is what loses the race, so the guard must not depend on it.
 *
 * Reconciles on submit only. In the normal case the id already matches and this is a
 * no-op, so the picker's own painting (price, availability, properties) is never
 * pre-empted - writing the id earlier would make `selectVariant` early-return and
 * suppress the `variant:change` those updates hang off.
 */
class VariantGuard extends HTMLElement {
  connectedCallback() {
    this._picker = this.closest("variant-picker");
    if (!this._picker) return;

    try {
      this._map = JSON.parse(
        this.querySelector('script[type="application/json"]')?.textContent ||
          "{}",
      );
    } catch {
      this._map = {};
    }

    // Capture phase, like <size-gate>: ProductForm builds its FormData in a target-phase
    // listener, so the id has to be corrected before that runs.
    document.addEventListener("submit", this._onSubmit, true);
  }

  disconnectedCallback() {
    document.removeEventListener("submit", this._onSubmit, true);
  }

  _onSubmit = (event) => {
    // Several pickers (quick-buy drawers) can share a page - only touch our own form.
    // Read the attribute, not `.id`: the form contains `<input name="id">`, and
    // named-property access shadows the `id` property with that element.
    if (event.target.getAttribute("id") !== this._picker.getAttribute("form")) {
      return;
    }

    const idInput = event.target.querySelector('input[name="id"]');
    if (!idInput) return;

    const expected = this._map[this._selectedKey()];
    if (expected == null || String(expected) === idInput.value) return;

    // `hide_sold_out_variants` templates: the picker deliberately refuses variants whose
    // market inventory is unavailable (`VariantPicker._isVariantSelectable`,
    // theme.js.liquid) and falls back to a substitute after `ProductLoader.load` lands.
    // Respect that substitution: never post a variant the picker would have rejected.
    if (
      this._picker.hasAttribute("hide-sold-out-variants") &&
      window.allVariantInventoryLevels?.[String(expected)]?.available === false
    ) {
      this._clearSizeProperties(event.target);
      return;
    }

    idInput.value = String(expected);
    this._syncPreorderProperty(event.target, idInput, String(expected));
  };

  /**
   * The picker's substitute id is what gets posted, but <size-gate> already wrote the size
   * line-item properties from the size the shopper tapped. `properties[_cart_display]`
   * renders in place of the variant title (snippets/line-item.liquid), so leaving it would
   * show the tapped size on a line that ships the substitute - order #207647's shape again.
   * Drop both so the line falls back to the posted variant's own title.
   */
  _clearSizeProperties(form) {
    const customerSize = form.querySelector("#customer-size");
    if (customerSize) {
      customerSize.setAttribute("name", "");
      customerSize.setAttribute("value", "");
    }
    form.querySelector("#cart-size-display")?.setAttribute("value", "");
  }

  /**
   * Keep `properties[Preorder]` agreeing with the id just written. The theme paints that
   * property from `variant:change` (theme.js.liquid), which never fires inside the race
   * window, so it would otherwise still carry the first variant's delivery promise.
   *
   * Reads the same server-rendered per-variant markup the theme reads, scoped to this
   * form: `.variant_info span[data-var-id]` for whether the variant is a pre-order, and
   * `.variant_preorder_timing span.pre_order_timing[data-var-id]` for its timing text.
   */
  _syncPreorderProperty(form, idInput, variantId) {
    const info = form.querySelector(
      `.variant_info span[data-var-id="${variantId}"]`,
    );
    if (!info) return;

    const timing = info.classList.contains("has_preorder")
      ? form
          .querySelector(
            `.variant_preorder_timing span.pre_order_timing[data-var-id="${variantId}"]`,
          )
          ?.textContent.trim()
      : "";

    const existing = form.querySelector('input[name="properties[Preorder]"]');

    if (!timing) {
      existing?.remove();
      return;
    }

    if (existing) {
      // Attribute, not property, so a later `variant:change` can still repaint it.
      existing.setAttribute("value", timing);
      return;
    }

    const input = document.createElement("input");
    input.type = "hidden";
    input.className = "pre_order_properties";
    input.name = "properties[Preorder]";
    input.setAttribute("value", timing);
    idInput.insertAdjacentElement("afterend", input);
  }

  /**
   * The selected option values, in option order, joined the way the rendered map is keyed.
   * Same element lookup `VariantPicker._getSelectedOptionValues` uses for the two shapes
   * this theme renders: the dropdown's hidden input (snippets/variant-picker.liquid) and
   * the swatch radios (snippets/swatch.liquid).
   */
  _selectedKey() {
    return Array.from(this._picker.querySelectorAll("[data-option-selector]"))
      .map(
        (selector) =>
          selector.querySelector(
            'input[name^="option"][type="hidden"], input[name^="option"]:checked',
          )?.value ?? "",
      )
      .join(" / ");
  }
}

if (!window.customElements.get("variant-guard")) {
  window.customElements.define("variant-guard", VariantGuard);
}
